import { Button, Card, FormGroup } from "@blueprintjs/core";
import { ItemSelect } from "@package/ui-apollo-components";
import {
  GetLayoutListNameIdDocument,
  type GetLayoutListNameIdQuery,
  type GetLayoutListNameIdQueryVariables,
  GetServiceListNameIdDocument,
  type GetServiceListNameIdQuery,
  type GetServiceListNameIdQueryVariables,
  type PostTileAsset,
  type PostTileAssetInput,
  ServiceTypes,
} from "@package/ui-gql/index-data-access";
import { produce } from "immer";
import { useMemo } from "react";
import { ServiceDataForm } from "../../../ServiceDataForm/ServiceDataForm";
import { LayoutCreateDrawer } from "../layout/LayoutEditDrawer";

type PostTileListEditorEntryProps = {
  disabled: boolean;
  tile: PostTileAsset;
  onTileRemoved: () => void;
  onTileUpdated: (tile: PostTileAsset) => void;
};

function PostTileListEditorEntry({
  disabled = false,
  onTileRemoved,
  onTileUpdated,
  tile,
}: PostTileListEditorEntryProps) {
  return (
    <Card compact>
      <FormGroup label="Content Type">
        <ItemSelect<
          GetServiceListNameIdQuery,
          GetServiceListNameIdQueryVariables
        >
          activeItemId={tile.tileType}
          disabled={disabled}
          document={GetServiceListNameIdDocument}
          onItemSelected={(id) =>
            onTileUpdated(
              produce(tile, (draft) => {
                draft.tileType = id;
              }),
            )
          }
          variables={{
            filter: {
              type: ServiceTypes.Tile,
            },
          }}
        />
      </FormGroup>
      <FormGroup label="Layout">
        <ItemSelect<GetLayoutListNameIdQuery, GetLayoutListNameIdQueryVariables>
          activeItemId={tile.layout}
          createDrawerElement={LayoutCreateDrawer}
          disabled={disabled}
          document={GetLayoutListNameIdDocument}
          onItemSelected={(id) =>
            onTileUpdated(
              produce(tile, (draft) => {
                draft.layout = id;
              }),
            )
          }
        />
      </FormGroup>
      <ServiceDataForm
        id={tile.tileType}
        appCmsType="post"
        card={false}
        data={tile}
        mediaId={tile.media}
        onMediaIdChanged={(mediaId) => {
          onTileUpdated(
            produce(tile, (draft) => {
              draft.media = mediaId;
            }),
          );
        }}
        onSourceChanged={(callback) =>
          onTileUpdated(
            produce(tile, (draft) => {
              callback(draft);
            }),
          )
        }
        showTitle={false}
      />
      <div className="flex flex-row justify-end">
        <Button
          disabled={disabled}
          minimal
          intent="danger"
          text="Delete Content"
          onClick={() => onTileRemoved()}
        />
      </div>
    </Card>
  );
}

type PostTileListEditorProps = {
  disabled?: boolean;
  tiles?: (PostTileAsset | null)[];
  onTilesChanged: (tiles: PostTileAssetInput[]) => void;
};

export function PostTileListEditor({
  disabled = false,
  tiles,
  onTilesChanged,
}: PostTileListEditorProps) {
  const tilesValid = useMemo<PostTileAsset[]>(
    () => tiles?.filter((t) => !!t) ?? [],
    [tiles],
  );

  return (
    <>
      {tilesValid?.map((tile, index) => (
        <div key={index} className="mb-4">
          <PostTileListEditorEntry
            disabled={disabled}
            tile={tile}
            onTileUpdated={(updated) => {
              onTilesChanged(
                produce(tilesValid, (draft) => {
                  draft[index] = updated;
                }),
              );
            }}
            onTileRemoved={() => {
              onTilesChanged(
                produce(tilesValid, (draft) => {
                  draft.splice(index, 1);
                }),
              );
            }}
          />
        </div>
      ))}
      <Button
        icon="add"
        intent="primary"
        minimal
        text="Add Content"
        onClick={() => {
          onTilesChanged(
            produce(tilesValid ?? [], (draft) => {
              draft.push({
                customData: {},
                layout: null,
                media: null,
                qrLinkPrefix: null,
                tileBackingImage: null,
                tileType: null,
              });
            }),
          );
        }}
      />
    </>
  );
}
